import React, { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './PillButton.css';

export type PillButtonVariant = 'primary' | 'secondary' | 'destructive' | 'legal';
export type PillButtonSize = 'default' | 'compact';

interface PillButtonBase {
  variant?: PillButtonVariant;
  size?: PillButtonSize;
  /** Action in flight: shows busyLabel, sets aria-busy, blocks repeat clicks. */
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-haspopup'?: 'dialog' | 'menu' | 'true';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'data-testid'?: string;
}

export interface PillLinkProps extends PillButtonBase {
  to: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  type?: never;
}

export interface PillActionProps extends PillButtonBase {
  to?: undefined;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
}

export type PillButtonProps = PillLinkProps | PillActionProps;

/**
 * The one pill button. Primary is white on black text and appears at most
 * once per visible screen; everything else is translucent. Busy keeps the
 * control focusable and width-stable while blocking duplicate submission.
 */
const PillButton: React.FC<PillButtonProps> = (props) => {
  const {
    variant = 'secondary',
    size = 'default',
    busy = false,
    busyLabel,
    disabled = false,
    icon,
    className,
    children,
    id,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-haspopup': ariaHasPopup,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'data-testid': testId,
  } = props;

  const classes = [
    'luma-pill',
    `luma-pill--${variant}`,
    size === 'compact' ? 'luma-pill--compact' : null,
    busy ? 'luma-pill--busy' : null,
    className || null,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {icon && <span className="luma-pill__icon" aria-hidden="true">{icon}</span>}
      <span className="luma-pill__labels">
        <span className="luma-pill__label" aria-hidden={busy || undefined}>{children}</span>
        {busyLabel && (
          <span className="luma-pill__label luma-pill__label--busy" aria-hidden={!busy || undefined}>
            {busyLabel}
          </span>
        )}
      </span>
    </>
  );

  const shared = {
    id,
    className: classes,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-haspopup': ariaHasPopup,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'aria-busy': busy || undefined,
    'data-variant': variant,
    'data-testid': testId,
  };

  if (props.to !== undefined && !disabled) {
    const { to, onClick } = props;
    return (
      <Link
        {...shared}
        to={to}
        onClick={(event) => {
          if (busy) {
            event.preventDefault();
            return;
          }
          onClick?.(event);
        }}
      >
        {content}
      </Link>
    );
  }

  const onClick = props.to === undefined ? props.onClick : undefined;
  const type = props.to === undefined ? props.type ?? 'button' : 'button';

  return (
    <button
      {...shared}
      type={type}
      disabled={disabled}
      onClick={(event) => {
        if (busy) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    >
      {content}
    </button>
  );
};

export default PillButton;
